<i18n>
{
  "fr": {
    "complete-subscribe-admin-title": "Complétion du profil administrateur",
    "complete-subscribe-user-title": "Complétion du profil utilisateur",
    "confirm-email-error-text": "Oh ! Une erreur s'est produite.",
    "user-password-rules": "Votre mot de passe doit avoir une longueur d'au moins 8 caractères, et contenir au moins un chiffre et une lettre majuscule.",
    "create-user-password-label": "Mot de passe",
    "create-user-passwordconfirmation-label": "Confirmation du mot de passe",
    "create-user-submit-btn": "Soumettre",
    "error-create-password-invalid": "Le mot de passe n'est pas valide",
    "error-create-password-different": "Le mot de passe doit être le même",
    "subscribe-user-error-user-not-found": "Ce compte utilisateur est introuvable.",
    "subscribe-user-error-invalid-invite-token": "Le lien que vous avez utilisé pour vous inscrire est expiré."
  },
  "en": {
    "complete-subscribe-admin-title": "Completion of administrator profile",
    "complete-subscribe-user-title": "Completion of user profile",
    "confirm-email-error-text": "Oh ! An error has occurred.",
    "user-password-rules": "Your password must be minimum 8 characters long, containing at least 1 digit and 1 upper case letter.",
    "create-user-password-label": "Password",
    "create-user-passwordconfirmation-label": "Password confirmation",
    "create-user-submit-btn": "Submit",
    "error-create-password-invalid": "The password is invalid.",
    "error-create-password-different": "The password must be the same.",
    "subscribe-user-error-user-not-found": "This user account was not found.",
    "subscribe-user-error-invalid-invite-token": "Your subscription link is expired."
  }
}
</i18n>

<template>
  <welcome>
    <h1 class="title" v-if="isBusinessAdmin">{{ $t('complete-subscribe-admin-title') }}</h1>
    <h1 class="title" v-else>{{ $t('complete-subscribe-user-title') }}</h1>
    <template v-if="showError">
      <p class="has-text-danger">{{ $t('confirm-email-error-text') }}</p>
    </template>
    <template v-else>
      <div class="content">
        <p class="is-size-5 has-text-primary">{{ email }}</p>
        <p>{{ $t('user-password-rules') }}</p>
      </div>
      <s-form ref="form" @submit="register">
        <s-field
          :label="$t('create-user-password-label')"
          label-for="password"
          :value="password"
          :rules="rules.passwordRules"
          validate-on-input
        >
          <b-input v-model="password" id="password" type="password" />
        </s-field>
        <s-field
          :label="$t('create-user-passwordconfirmation-label')"
          label-for="passwordConfirmation"
          :value="passwordConfirmation"
          :rules="rules.passwordConfirmationRules"
          validate-on-input
        >
          <b-input v-model="passwordConfirmation" id="passwordConfirmation" type="password" />
        </s-field>
        <p class="mt-32px">
          <b-button
            class="is-fullwidth"
            type="is-link"
            outlined
            size="is-medium"
            tag="input"
            native-type="submit"
            :value="$t('create-user-submit-btn')"
          />
        </p>
      </s-form>
    </template>
  </welcome>
</template>

<script>
import SForm from '@/components/form/form'
import SField from '@/components/form/field'
import Welcome from '@/components/layout/welcome'

import AuthentificationService from '@/services/authentification'
import BusinessService from '@/services/business'
import UserService from '@/services/user'

import { URL_ACCOUNT_LOGIN, URL_ACCOUNT_APPLICANT_FIRST_CONNECTION } from '@/consts/urls'
import { TOKEN_STATUS_INVALID, TOKEN_STATUS_USER_CONFIRMED, TOKEN_TYPE_CREATE_USER } from '@/consts/enums'
import FormHelper from '@/helpers/form'

export default {
  data() {
    return {
      rules: {
        passwordRules: [(value) => FormHelper.isValidPassword(value) || this.$t('error-create-password-invalid')],
        passwordConfirmationRules: [
          (value) => FormHelper.isSame(this.password, value) || this.$t('error-create-password-different')
        ]
      },
      token: this.$router.currentRoute.query.token,
      email: this.$router.currentRoute.query.email,
      userType: this.$router.currentRoute.query.userType,
      isApplicant: this.$router.currentRoute.query.isApplicant,
      businessId: this.$router.currentRoute.query.businessId,
      showError: false,
      password: '',
      passwordConfirmation: ''
    }
  },
  components: { SForm, SField, Welcome },
  async mounted() {
    let result = await UserService.verifyToken(this.email, this.token, TOKEN_TYPE_CREATE_USER)

    if (result.status === TOKEN_STATUS_INVALID) {
      this.showError = true
    } else if (result.status === TOKEN_STATUS_USER_CONFIRMED) {
      this.$router.push({
        name: URL_ACCOUNT_LOGIN,
        query: { email: this.email }
      })
    }
  },
  computed: {
    isBusinessAdmin: function () {
      return this.userType === 'BusinessAdmin'
    }
  },
  methods: {
    register: async function () {
      if (this.$refs.form.validate()) {
        await this.$apollo.mutate({
          mutation: this.$options.mutation.CompleteUserRegistration,
          variables: {
            input: {
              emailAddress: this.email,
              inviteToken: this.token,
              password: this.password
            }
          }
        })

        let resolver = null

        if (this.isApplicant) {
          resolver = async () => {
            await BusinessService.setCurrentBusinessData(this.businessId)
            this.$router.push({
              name: URL_ACCOUNT_APPLICANT_FIRST_CONNECTION
            })
          }
        }

        await AuthentificationService.login(this.email, this.password, resolver)
      }
    }
  },
  gqlErrors() {
    return {
      USER_NOT_FOUND: this.$t('subscribe-user-error-user-not-found'),
      INVALID_INVITE_TOKEN: this.$t('subscribe-user-error-invalid-invite-token')
    }
  }
}
</script>

<graphql>
mutation CompleteUserRegistration($input: CompleteUserRegistrationInput!) {
  completeUserRegistration(input: $input) {
    user {
      id
    }
  }
}
</graphql>
